import type {
  RecoveryMovementEvidence,
  RecoveryStoredOutcome,
} from '@/lib/api/schemas/product-recovery.schema'

/**
 * Reading `movementEvidence` into what a decision actually moved.
 *
 * The server stores an open JSON object whose keys depend on the outcome: a
 * match carries the source and target relation rows, a deletion carries the
 * deleted row, and a targetless finding carries the candidates it was taken
 * over. Everything below narrows `unknown` rather than casting it, so a shape
 * the server changes later degrades to "nothing recorded" instead of to a lie.
 *
 * A decision without what it moved is a claim, so this is what the panel renders
 * instead of the object itself.
 */

export type RelationMovement = { relation: string; rows: number }

export type MovementSummary =
  | {
    kind: 'moved'
    targetProductId: string | null
    moved: readonly RelationMovement[]
    /** Deliberately left on the retired row: its own description, and merges the owner holds. */
    retained: readonly RelationMovement[]
  }
  | {
    kind: 'distributed'
    targets: readonly { productId: string; rows: number }[]
    retained: readonly RelationMovement[]
  }
  | { kind: 'deleted' }
  | { kind: 'nothing_moved' }

/**
 * The two relations the mover never touches. `forge.product_content` is the
 * archived subject's own description, kept as the operator saw it, and
 * `forge.product` holds incoming merges the owner decides about. Counting either
 * as moved would claim a movement that deliberately did not happen.
 */
const RETAINED_RELATIONS: readonly string[] = ['forge.product_content', 'forge.product']

function rowsByRelation(value: unknown): Map<string, number> {
  const counts = new Map<string, number>()
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return counts
  for (const [relation, rows] of Object.entries(value)) {
    if (Array.isArray(rows) && rows.length > 0) counts.set(relation, rows.length)
  }
  return counts
}

function countOf(value: unknown): number {
  return Array.isArray(value) ? value.length : 0
}

function ordered(counts: Iterable<readonly [string, number]>): RelationMovement[] {
  return [...counts]
    .map(([relation, rows]) => ({ relation, rows }))
    .sort((left, right) => right.rows - left.rows || left.relation.localeCompare(right.relation))
}

function textValue(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function summariseMovement(
  outcome: RecoveryStoredOutcome,
  evidence: RecoveryMovementEvidence,
): MovementSummary {
  if (outcome === 'deleted') return { kind: 'deleted' }
  if (outcome === 'distributed') {
    const targets = new Map<string, number>()
    const moves = evidence.moves
    if (Array.isArray(moves)) {
      for (const move of moves) {
        if (typeof move !== 'object' || move === null || Array.isArray(move)) continue
        if (!('targetProductId' in move)) continue
        const target = textValue(move.targetProductId)
        if (target === null) continue
        targets.set(target, (targets.get(target) ?? 0) + 1)
      }
    }
    const retained = rowsByRelation(evidence.retained)
    return {
      kind: 'distributed',
      targets: [...targets.entries()]
        .map(([productId, rows]) => ({ productId, rows }))
        .sort((left, right) => left.productId.localeCompare(right.productId)),
      retained: ordered(retained),
    }
  }
  if (outcome !== 'matched') return { kind: 'nothing_moved' }

  const source = rowsByRelation(evidence.sourceBefore)
  const moved = ordered([...source].filter(([relation]) => !RETAINED_RELATIONS.includes(relation)))
  const retainedCounts = new Map<string, number>()
  const description = countOf(evidence.retainedOriginalDescription)
  const merges = countOf(evidence.ownerHeldIncomingMerges)
  if (description > 0) retainedCounts.set('forge.product_content', description)
  if (merges > 0) retainedCounts.set('forge.product', merges)

  return {
    kind: 'moved',
    targetProductId: textValue(evidence.targetProductId),
    moved,
    retained: ordered(retainedCounts),
  }
}
